import { DayPickerPopover } from 'components/molecules/DayPickerPopover';
import {
  BottomSheetSessionSelector,
  BottomSheetDatePicker,
} from 'components/molecules/BottomDrawer';
import { Flex, SrOnly } from 'components/common/styled/common-styled';
import { SessionTimeOption } from 'features/Reserve/hooks/useSessionTimeOptions';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RadixSelect } from 'components/common/RadixSelect';
import styled from 'styled-components';
import { Session } from 'types/listings';
import { DATE_FORMAT } from 'utils';
import { useIsMobile } from 'WindowDimensionProvider';
import { DateInput } from 'components/atoms/DateInput';

export interface SessionSelectorProps {
  sessionsTimeOptions: SessionTimeOption[];
  selectedDate: string;
  selectedTime: string;
  selectedDuration: number;
  availableDates: string[];
  isLoadingAvailableDates: boolean;
  onMonthChange: (date: moment.Moment) => void;
  onChangeSelectedDate: (date: string) => void;
  onChangeSelectedSession: (time: string, duration: number) => void;
}

const ResourceSessionSelector = ({
  sessionsTimeOptions,
  selectedDate,
  selectedTime,
  selectedDuration,
  isLoadingAvailableDates,
  availableDates,
  onMonthChange,
  onChangeSelectedDate,
  onChangeSelectedSession,
}: SessionSelectorProps) => {
  const { t: translate } = useTranslation();
  const selectControllerRef = useRef<HTMLDivElement>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => {
    selectControllerRef.current?.children?.[0]?.setAttribute(
      'aria-label',
      translate('selectSession'),
    );
  }, [selectControllerRef, translate]);

  const selectedSessionOption = useMemo(
    () =>
      sessionsTimeOptions.find((timeOption) => {
        const [sessionTime, sessionDuration] = timeOption.value.split('-');
        return (
          selectedTime === sessionTime &&
          selectedDuration === parseInt(sessionDuration, 10)
        );
      }),
    [sessionsTimeOptions, selectedTime, selectedDuration],
  );

  const isMobile = useIsMobile();

  const handleSessionSelect = (session: Session) => {
    onChangeSelectedSession(
      session.startDateTime.split('T')[1],
      session.duration,
    );
  };

  const selectedSession = useMemo(() => {
    return sessionsTimeOptions.find(
      (o) => o.value === `${selectedTime}-${selectedDuration}`,
    )?.original;
  }, [sessionsTimeOptions, selectedTime, selectedDuration]);

  // No custom Control needed for RadixSelect

  const isDateBlocked = (date: moment.Moment) => {
    return !availableDates.includes(date.format(DATE_FORMAT));
  };

  const onSelectDate = (date: Date) => {
    if (isLoadingAvailableDates) return;
    onChangeSelectedDate(moment(date).format(DATE_FORMAT));
  };

  return (
    <SelectWrapper
      direction="row"
      gap={12}
      alignItem="stretch"
      justifyContent="flex-start"
    >
      {isMobile ? (
        <BottomSheetDatePicker
          availableDates={availableDates}
          onChange={(option) => {
            if (option) {
              onChangeSelectedDate(moment(option).format(DATE_FORMAT));
            }
          }}
          onMonthChange={onMonthChange}
          onOpenChange={setShowDatePicker}
          open={showDatePicker}
          value={selectedDate}
          isCalendarLoading={isLoadingAvailableDates}
        />
      ) : (
        <PopoverPickerWrapper>
          <DayPickerPopover
            isLoading={isLoadingAvailableDates}
            open={showDatePicker}
            onOpenChange={setShowDatePicker}
            mode="single"
            required
            asChild
            defaultMonth={new Date(`${selectedDate}T00:00:00`)}
            onSelect={onSelectDate}
            className="resource-session__date-picker-popover"
            sideOffset={10}
            footer={null}
            selected={
              !isDateBlocked(moment(selectedDate))
                ? moment(selectedDate).toDate()
                : undefined
            }
            onNextClick={(date) => onMonthChange(moment(date))}
            onPrevClick={(date) => onMonthChange(moment(date))}
            modifiers={{
              disabled: (date) =>
                isDateBlocked(moment(date)) || isLoadingAvailableDates,
            }}
            trigger={
              <DateInput
                date={selectedDate ? moment(selectedDate).toDate() : null}
                id="date-input-resource-session-selector"
                dateFormat="MMMM d"
                placeholder={translate('selectDate')}
              />
            }
          />
        </PopoverPickerWrapper>
      )}

      {isMobile ? (
        <BottomSheetSessionSelector
          sessions={sessionsTimeOptions.map((o) => o.original)}
          onSessionClick={handleSessionSelect}
          selectedSession={selectedSession}
        />
      ) : (
        <StyledLabel>
          <RadixSelect
            aria-label={translate('selectSession')}
            options={sessionsTimeOptions}
            value={selectedSessionOption?.value || ''}
            placeholder={translate(
              !sessionsTimeOptions.length ? 'unavailable' : 'select',
            )}
            disabled={!sessionsTimeOptions.length}
            onValueChange={(value) => {
              const option = sessionsTimeOptions.find(
                (opt) => opt.value === value,
              );
              if (option) {
                const [sessionTime, sessionDuration] = option.value.split('-');
                onChangeSelectedSession(
                  sessionTime,
                  parseInt(sessionDuration, 10),
                );
              }
            }}
            className="resource-item__session-times"
          />
          <SrOnly>{translate('selectSession')}</SrOnly>
        </StyledLabel>
      )}
    </SelectWrapper>
  );
};

const StyledLabel = styled.label`
  margin-bottom: 0;
`;

const SelectWrapper = styled(Flex)`
  & > * {
    flex: 1;
  }

  & .resource-item-sessions__control {
    &:hover {
      border-color: var(--way-palette-black-40);
      background: var(--way-palette-black-5);
    }
  }

  & .resource-item-sessions__indicator {
    padding: 0;
  }
`;

const PopoverPickerWrapper = styled.div`
  & div[role='button'] {
    padding: 12px 14px;
  }

  & #date-input-resource-session-selector {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0px;
  }
`;

export default ResourceSessionSelector;
